/**
 * Predefined architecture diagram templates for each CISA pillar
 *
 * Each template includes:
 * - nodes: Array of technology nodes with positions
 * - edges: Array of connections between nodes
 *
 * Positions are designed for a ~800x600 canvas
 */

/**
 * Identity Pillar Template
 *
 * Flow: Users/Devices → Identity Provider → MFA → Access Management → Applications
 */
const IDENTITY_TEMPLATE = {
  nodes: [
    // Input nodes (left side)
    { id: 'identity-users', label: 'Users', icon: 'users', category: 'input', isInput: true, x: 60, y: 150 },
    { id: 'identity-devices', label: 'Devices', icon: 'laptop', category: 'input', isInput: true, x: 60, y: 280 },

    // Core identity stack
    { id: 'identity-idp', label: 'Identity Provider', vendorName: 'Okta', productName: 'Okta Identity Cloud', icon: 'key', category: 'idp', x: 240, y: 200 },
    { id: 'identity-mfa', label: 'Multi-Factor Auth', vendorName: 'Various', productName: 'MFA', icon: 'shield-check', category: 'mfa', x: 400, y: 120 },
    { id: 'identity-access', label: 'Access Management', vendorName: 'SailPoint', productName: 'SailPoint IdentityIQ', icon: 'user-check', category: 'access', x: 400, y: 280 },
    { id: 'identity-pam', label: 'Privileged Access', vendorName: 'CyberArk', productName: 'CyberArk PAM', icon: 'shield', category: 'pam', x: 560, y: 200 },
    { id: 'identity-directory', label: 'Directory Services', vendorName: 'Microsoft', productName: 'Microsoft Entra ID', icon: 'database', category: 'directory', x: 240, y: 380 },
    { id: 'identity-analytics', label: 'Security Analytics', vendorName: 'Splunk', productName: 'Splunk SIEM', icon: 'activity', category: 'analytics', x: 400, y: 420 },

    // Output node (right side)
    { id: 'identity-apps', label: 'Applications', icon: 'layers', category: 'output', isOutput: true, x: 720, y: 200 }
  ],
  edges: [
    // Users/Devices to IdP
    { id: 'e-users-idp', sourceId: 'identity-users', targetId: 'identity-idp', type: 'DATA_FLOW', label: 'Auth Request' },
    { id: 'e-devices-idp', sourceId: 'identity-devices', targetId: 'identity-idp', type: 'DATA_FLOW', label: 'Device Auth' },

    // IdP to MFA
    { id: 'e-idp-mfa', sourceId: 'identity-idp', targetId: 'identity-mfa', type: 'DATA_FLOW', label: 'MFA Challenge' },

    // IdP to Access Management
    { id: 'e-idp-access', sourceId: 'identity-idp', targetId: 'identity-access', type: 'INTEGRATION' },

    // Access to PAM
    { id: 'e-access-pam', sourceId: 'identity-access', targetId: 'identity-pam', type: 'TRUST_CONTROL', label: 'Elevation' },

    // Directory integrations
    { id: 'e-directory-idp', sourceId: 'identity-directory', targetId: 'identity-idp', type: 'INTEGRATION', label: 'Sync' },

    // Analytics connections
    { id: 'e-idp-analytics', sourceId: 'identity-idp', targetId: 'identity-analytics', type: 'DATA_FLOW', label: 'Logs' },
    { id: 'e-access-analytics', sourceId: 'identity-access', targetId: 'identity-analytics', type: 'DATA_FLOW' },
    { id: 'e-pam-analytics', sourceId: 'identity-pam', targetId: 'identity-analytics', type: 'DATA_FLOW' },

    // To Applications
    { id: 'e-pam-apps', sourceId: 'identity-pam', targetId: 'identity-apps', type: 'DATA_FLOW', label: 'Authorized' },
    { id: 'e-mfa-apps', sourceId: 'identity-mfa', targetId: 'identity-apps', type: 'TRUST_CONTROL' }
  ]
};

/**
 * Devices Pillar Template
 *
 * Flow: Devices → MDM/UEM → Compliance → Threat Protection → Resources
 */
const DEVICES_TEMPLATE = {
  nodes: [
    // Input nodes
    { id: 'devices-managed', label: 'Managed Devices', icon: 'laptop', category: 'input', isInput: true, x: 60, y: 150 },
    { id: 'devices-byod', label: 'BYOD', icon: 'smartphone', category: 'input', isInput: true, x: 60, y: 280 },

    // Core device stack
    { id: 'devices-mdm', label: 'MDM / UEM', vendorName: 'Microsoft', productName: 'Microsoft Intune', icon: 'settings', category: 'mdm', x: 240, y: 200 },
    { id: 'devices-compliance', label: 'Compliance Engine', vendorName: 'Tanium', productName: 'Tanium Comply', icon: 'check-circle', category: 'compliance', x: 400, y: 120 },
    { id: 'devices-edr', label: 'EDR / XDR', vendorName: 'CrowdStrike', productName: 'CrowdStrike Falcon', icon: 'shield-alert', category: 'edr', x: 400, y: 280 },
    { id: 'devices-asset', label: 'Asset Inventory', vendorName: 'Axonius', productName: 'Axonius', icon: 'package', category: 'asset', x: 240, y: 380 },
    { id: 'devices-ztna', label: 'ZTNA Gateway', vendorName: 'Zscaler', productName: 'Zscaler Private Access', icon: 'lock', category: 'ztna', x: 560, y: 200 },

    // Output node
    { id: 'devices-resources', label: 'Enterprise Resources', icon: 'server', category: 'output', isOutput: true, x: 720, y: 200 }
  ],
  edges: [
    // Devices to MDM
    { id: 'e-managed-mdm', sourceId: 'devices-managed', targetId: 'devices-mdm', type: 'DATA_FLOW', label: 'Enrollment' },
    { id: 'e-byod-mdm', sourceId: 'devices-byod', targetId: 'devices-mdm', type: 'DATA_FLOW', label: 'MAM' },

    // MDM to Compliance
    { id: 'e-mdm-compliance', sourceId: 'devices-mdm', targetId: 'devices-compliance', type: 'DATA_FLOW', label: 'Posture' },

    // MDM to EDR
    { id: 'e-mdm-edr', sourceId: 'devices-mdm', targetId: 'devices-edr', type: 'INTEGRATION' },

    // Asset inventory
    { id: 'e-mdm-asset', sourceId: 'devices-mdm', targetId: 'devices-asset', type: 'DATA_FLOW', label: 'Inventory' },
    { id: 'e-edr-asset', sourceId: 'devices-edr', targetId: 'devices-asset', type: 'DATA_FLOW' },

    // Compliance to ZTNA
    { id: 'e-compliance-ztna', sourceId: 'devices-compliance', targetId: 'devices-ztna', type: 'TRUST_CONTROL', label: 'Trust Score' },
    { id: 'e-edr-ztna', sourceId: 'devices-edr', targetId: 'devices-ztna', type: 'TRUST_CONTROL' },

    // ZTNA to Resources
    { id: 'e-ztna-resources', sourceId: 'devices-ztna', targetId: 'devices-resources', type: 'DATA_FLOW', label: 'Access' }
  ]
};

/**
 * Networks Pillar Template
 *
 * Flow: Traffic → Segmentation → NDR → Encryption → Applications
 */
const NETWORKS_TEMPLATE = {
  nodes: [
    // Input nodes
    { id: 'networks-users', label: 'Users', icon: 'users', category: 'input', isInput: true, x: 60, y: 150 },
    { id: 'networks-workloads', label: 'Workloads', icon: 'box', category: 'input', isInput: true, x: 60, y: 280 },

    // Core network stack
    { id: 'networks-seg', label: 'Network Segmentation', vendorName: 'Cisco', productName: 'Cisco ISE / TrustSec', icon: 'git-branch', category: 'segmentation', x: 240, y: 200 },
    { id: 'networks-ndr', label: 'NDR / Traffic Analysis', vendorName: 'Cisco', productName: 'Secure Network Analytics', icon: 'activity', category: 'ndr', x: 400, y: 120 },
    { id: 'networks-microseg', label: 'Microsegmentation', vendorName: 'Illumio', productName: 'Illumio Core', icon: 'grid', category: 'microseg', x: 400, y: 280 },
    { id: 'networks-encrypt', label: 'Traffic Encryption', vendorName: 'Various', productName: 'mTLS / MACsec', icon: 'lock', category: 'encryption', x: 560, y: 200 },
    { id: 'networks-ddos', label: 'DDoS Protection', vendorName: 'Cloudflare', productName: 'Cloudflare Magic Transit', icon: 'shield', category: 'resilience', x: 240, y: 380 },

    // Output node
    { id: 'networks-apps', label: 'Applications', icon: 'layers', category: 'output', isOutput: true, x: 720, y: 200 }
  ],
  edges: [
    // Users/Workloads to Segmentation
    { id: 'e-users-seg', sourceId: 'networks-users', targetId: 'networks-seg', type: 'DATA_FLOW' },
    { id: 'e-workloads-seg', sourceId: 'networks-workloads', targetId: 'networks-seg', type: 'DATA_FLOW' },

    // Segmentation to NDR
    { id: 'e-seg-ndr', sourceId: 'networks-seg', targetId: 'networks-ndr', type: 'DATA_FLOW', label: 'Telemetry' },

    // Segmentation to Microseg
    { id: 'e-seg-microseg', sourceId: 'networks-seg', targetId: 'networks-microseg', type: 'TRUST_CONTROL' },

    // NDR integration
    { id: 'e-microseg-ndr', sourceId: 'networks-microseg', targetId: 'networks-ndr', type: 'DATA_FLOW' },

    // To Encryption
    { id: 'e-seg-encrypt', sourceId: 'networks-seg', targetId: 'networks-encrypt', type: 'DATA_FLOW' },
    { id: 'e-microseg-encrypt', sourceId: 'networks-microseg', targetId: 'networks-encrypt', type: 'DATA_FLOW' },

    // DDoS protection
    { id: 'e-ddos-seg', sourceId: 'networks-ddos', targetId: 'networks-seg', type: 'TRUST_CONTROL', label: 'Scrubbed Traffic' },

    // Encryption to Apps
    { id: 'e-encrypt-apps', sourceId: 'networks-encrypt', targetId: 'networks-apps', type: 'DATA_FLOW', label: 'Secure' }
  ]
};

/**
 * Applications Pillar Template
 *
 * Flow: Users → ZTNA → WAF/RASP → App Security → Applications
 */
const APPLICATIONS_TEMPLATE = {
  nodes: [
    // Input nodes
    { id: 'apps-users', label: 'Users', icon: 'users', category: 'input', isInput: true, x: 60, y: 150 },
    { id: 'apps-api', label: 'API Clients', icon: 'code', category: 'input', isInput: true, x: 60, y: 280 },

    // Core application stack
    { id: 'apps-ztna', label: 'ZTNA / SSO', vendorName: 'Zscaler', productName: 'Zscaler ZPA', icon: 'log-in', category: 'ztna', x: 240, y: 200 },
    { id: 'apps-waf', label: 'WAF / API Gateway', vendorName: 'Cloudflare', productName: 'Cloudflare WAF', icon: 'shield', category: 'waf', x: 400, y: 120 },
    { id: 'apps-rasp', label: 'RASP', vendorName: 'Contrast', productName: 'Contrast Security', icon: 'shield-alert', category: 'rasp', x: 400, y: 280 },
    { id: 'apps-sast', label: 'SAST / DAST', vendorName: 'Snyk', productName: 'Snyk Code', icon: 'search', category: 'devsec', x: 240, y: 380 },
    { id: 'apps-sca', label: 'SCA / SBOM', vendorName: 'Various', productName: 'Software Composition', icon: 'package', category: 'devsec', x: 400, y: 380 },

    // Output node
    { id: 'apps-workloads', label: 'App Workloads', icon: 'layers', category: 'output', isOutput: true, x: 720, y: 200 }
  ],
  edges: [
    // Users/API to ZTNA
    { id: 'e-users-ztna', sourceId: 'apps-users', targetId: 'apps-ztna', type: 'DATA_FLOW', label: 'Auth' },
    { id: 'e-api-ztna', sourceId: 'apps-api', targetId: 'apps-ztna', type: 'DATA_FLOW' },

    // ZTNA to WAF
    { id: 'e-ztna-waf', sourceId: 'apps-ztna', targetId: 'apps-waf', type: 'DATA_FLOW' },

    // ZTNA to RASP
    { id: 'e-ztna-rasp', sourceId: 'apps-ztna', targetId: 'apps-rasp', type: 'DATA_FLOW' },

    // WAF/RASP to workloads
    { id: 'e-waf-workloads', sourceId: 'apps-waf', targetId: 'apps-workloads', type: 'DATA_FLOW', label: 'Protected' },
    { id: 'e-rasp-workloads', sourceId: 'apps-rasp', targetId: 'apps-workloads', type: 'TRUST_CONTROL' },

    // DevSec pipeline
    { id: 'e-sast-sca', sourceId: 'apps-sast', targetId: 'apps-sca', type: 'INTEGRATION', label: 'Pipeline' },
    { id: 'e-sca-workloads', sourceId: 'apps-sca', targetId: 'apps-workloads', type: 'TRUST_CONTROL', label: 'Deploy Gate' }
  ]
};

/**
 * Data Pillar Template
 *
 * Flow: Data Sources → Classification → DLP → Encryption → Data Access
 */
const DATA_TEMPLATE = {
  nodes: [
    // Input nodes
    { id: 'data-structured', label: 'Structured Data', icon: 'database', category: 'input', isInput: true, x: 60, y: 150 },
    { id: 'data-unstructured', label: 'Unstructured Data', icon: 'file-text', category: 'input', isInput: true, x: 60, y: 280 },

    // Core data stack
    { id: 'data-discovery', label: 'Data Discovery', vendorName: 'BigID', productName: 'BigID', icon: 'search', category: 'discovery', x: 240, y: 200 },
    { id: 'data-classify', label: 'Classification', vendorName: 'Microsoft', productName: 'Microsoft Purview', icon: 'tag', category: 'classification', x: 400, y: 120 },
    { id: 'data-dlp', label: 'DLP', vendorName: 'Varonis', productName: 'Varonis DatAdvantage', icon: 'eye-off', category: 'dlp', x: 400, y: 280 },
    { id: 'data-encrypt', label: 'Encryption / KMS', vendorName: 'HashiCorp', productName: 'HashiCorp Vault', icon: 'lock', category: 'encryption', x: 560, y: 200 },
    { id: 'data-backup', label: 'Backup / DR', vendorName: 'Veeam', productName: 'Veeam', icon: 'archive', category: 'backup', x: 240, y: 380 },

    // Output node
    { id: 'data-access', label: 'Authorized Access', icon: 'check-circle', category: 'output', isOutput: true, x: 720, y: 200 }
  ],
  edges: [
    // Data sources to discovery
    { id: 'e-structured-discovery', sourceId: 'data-structured', targetId: 'data-discovery', type: 'DATA_FLOW' },
    { id: 'e-unstructured-discovery', sourceId: 'data-unstructured', targetId: 'data-discovery', type: 'DATA_FLOW' },

    // Discovery to Classification
    { id: 'e-discovery-classify', sourceId: 'data-discovery', targetId: 'data-classify', type: 'DATA_FLOW', label: 'Inventory' },

    // Discovery to DLP
    { id: 'e-discovery-dlp', sourceId: 'data-discovery', targetId: 'data-dlp', type: 'INTEGRATION' },

    // Classification to DLP
    { id: 'e-classify-dlp', sourceId: 'data-classify', targetId: 'data-dlp', type: 'TRUST_CONTROL', label: 'Labels' },

    // DLP to Encryption
    { id: 'e-dlp-encrypt', sourceId: 'data-dlp', targetId: 'data-encrypt', type: 'DATA_FLOW' },

    // Classification to Encryption
    { id: 'e-classify-encrypt', sourceId: 'data-classify', targetId: 'data-encrypt', type: 'TRUST_CONTROL' },

    // Backup connections
    { id: 'e-discovery-backup', sourceId: 'data-discovery', targetId: 'data-backup', type: 'DATA_FLOW' },
    { id: 'e-backup-encrypt', sourceId: 'data-backup', targetId: 'data-encrypt', type: 'INTEGRATION' },

    // Encryption to Access
    { id: 'e-encrypt-access', sourceId: 'data-encrypt', targetId: 'data-access', type: 'DATA_FLOW', label: 'Protected' }
  ]
};

/**
 * All pillar templates indexed by pillar code
 */
export const PILLAR_TEMPLATES = {
  IDENTITY: IDENTITY_TEMPLATE,
  DEVICES: DEVICES_TEMPLATE,
  NETWORKS: NETWORKS_TEMPLATE,
  APPLICATIONS: APPLICATIONS_TEMPLATE,
  DATA: DATA_TEMPLATE
};

/**
 * Get template for a specific pillar
 * @param {string} pillarCode - The pillar code (e.g., 'IDENTITY')
 * @returns {Object} Template with nodes and edges
 */
export function getTemplateForPillar(pillarCode) {
  return PILLAR_TEMPLATES[pillarCode] || IDENTITY_TEMPLATE;
}

/**
 * Available product categories for the node palette
 */
export const PRODUCT_CATEGORIES = {
  IDENTITY: [
    { key: 'idp', label: 'Identity Provider', icon: 'key' },
    { key: 'mfa', label: 'Multi-Factor Auth', icon: 'shield-check' },
    { key: 'access', label: 'Access Management', icon: 'user-check' },
    { key: 'pam', label: 'Privileged Access', icon: 'shield' },
    { key: 'directory', label: 'Directory', icon: 'database' },
    { key: 'analytics', label: 'Analytics', icon: 'activity' }
  ],
  DEVICES: [
    { key: 'mdm', label: 'MDM / UEM', icon: 'settings' },
    { key: 'compliance', label: 'Compliance', icon: 'check-circle' },
    { key: 'edr', label: 'EDR / XDR', icon: 'shield-alert' },
    { key: 'asset', label: 'Asset Inventory', icon: 'package' },
    { key: 'ztna', label: 'ZTNA', icon: 'lock' }
  ],
  NETWORKS: [
    { key: 'segmentation', label: 'Segmentation', icon: 'git-branch' },
    { key: 'ndr', label: 'NDR', icon: 'activity' },
    { key: 'microseg', label: 'Microsegmentation', icon: 'grid' },
    { key: 'encryption', label: 'Encryption', icon: 'lock' },
    { key: 'resilience', label: 'Resilience', icon: 'shield' }
  ],
  APPLICATIONS: [
    { key: 'ztna', label: 'ZTNA / SSO', icon: 'log-in' },
    { key: 'waf', label: 'WAF', icon: 'shield' },
    { key: 'rasp', label: 'RASP', icon: 'shield-alert' },
    { key: 'devsec', label: 'DevSecOps', icon: 'search' }
  ],
  DATA: [
    { key: 'discovery', label: 'Discovery', icon: 'search' },
    { key: 'classification', label: 'Classification', icon: 'tag' },
    { key: 'dlp', label: 'DLP', icon: 'eye-off' },
    { key: 'encryption', label: 'Encryption', icon: 'lock' },
    { key: 'backup', label: 'Backup / DR', icon: 'archive' }
  ]
};

/**
 * Sample products that can be added to the diagram
 * These are suggestions based on CISA ZTMM recommendations
 * Expanded catalog of 150+ products across all major Zero Trust vendors
 */
export const SAMPLE_PRODUCTS = {
  IDENTITY: [
    // Identity Providers (idp)
    { name: 'Okta', product: 'Okta Identity Cloud', category: 'idp' },
    { name: 'Microsoft', product: 'Microsoft Entra ID', category: 'idp' },
    { name: 'Ping Identity', product: 'PingOne', category: 'idp' },
    { name: 'Google', product: 'Google Cloud Identity', category: 'idp' },
    { name: 'AWS', product: 'AWS IAM Identity Center', category: 'idp' },
    { name: 'OneLogin', product: 'OneLogin', category: 'idp' },
    { name: 'JumpCloud', product: 'JumpCloud', category: 'idp' },
    { name: 'ForgeRock', product: 'ForgeRock Identity', category: 'idp' },
    // Multi-Factor Auth (mfa)
    { name: 'Cisco', product: 'Cisco Duo', category: 'mfa' },
    { name: 'Microsoft', product: 'Microsoft Authenticator', category: 'mfa' },
    { name: 'RSA', product: 'RSA SecurID', category: 'mfa' },
    { name: 'Thales', product: 'Thales SafeNet', category: 'mfa' },
    { name: 'Entrust', product: 'Entrust Identity', category: 'mfa' },
    { name: 'Entrust', product: 'Entrust Grid Card (Legacy)', category: 'mfa' },
    { name: 'HYPR', product: 'HYPR Passwordless', category: 'mfa' },
    { name: 'Yubico', product: 'YubiKey (FIDO2)', category: 'mfa' },
    // Access Management (access)
    { name: 'SailPoint', product: 'SailPoint IdentityIQ', category: 'access' },
    { name: 'Saviynt', product: 'Saviynt EIC', category: 'access' },
    { name: 'One Identity', product: 'One Identity Manager', category: 'access' },
    { name: 'Omada', product: 'Omada Identity', category: 'access' },
    { name: 'IBM', product: 'IBM Verify Governance', category: 'access' },
    // Privileged Access Management (pam)
    { name: 'CyberArk', product: 'CyberArk PAM', category: 'pam' },
    { name: 'BeyondTrust', product: 'BeyondTrust', category: 'pam' },
    { name: 'Delinea', product: 'Delinea Secret Server', category: 'pam' },
    { name: 'HashiCorp', product: 'HashiCorp Boundary', category: 'pam' },
    { name: 'Teleport', product: 'Teleport Access', category: 'pam' },
    { name: 'StrongDM', product: 'StrongDM', category: 'pam' },
    // Directory Services (directory)
    { name: 'Microsoft', product: 'Active Directory', category: 'directory' },
    { name: 'Ping Identity', product: 'PingDirectory', category: 'directory' },
    { name: 'AWS', product: 'AWS Directory Service', category: 'directory' },
    { name: 'Radiant Logic', product: 'Radiant Logic VDS', category: 'directory' },
    // Security Analytics (analytics)
    { name: 'Splunk', product: 'Splunk SIEM', category: 'analytics' },
    { name: 'Cisco', product: 'Splunk SIEM (Cisco)', category: 'analytics' },
    { name: 'Microsoft', product: 'Microsoft Sentinel', category: 'analytics' },
    { name: 'Palo Alto', product: 'Cortex XSIAM', category: 'analytics' },
    { name: 'Google', product: 'Google Chronicle', category: 'analytics' },
    { name: 'Elastic', product: 'Elastic Security', category: 'analytics' },
    { name: 'CrowdStrike', product: 'Falcon LogScale', category: 'analytics' },
    { name: 'Securonix', product: 'Securonix SIEM', category: 'analytics' },
    { name: 'Exabeam', product: 'Exabeam SIEM', category: 'analytics' },
    { name: 'LogRhythm', product: 'LogRhythm SIEM', category: 'analytics' },
    { name: 'Rapid7', product: 'Rapid7 InsightIDR', category: 'analytics' },
    { name: 'Sumo Logic', product: 'Sumo Logic', category: 'analytics' },
    { name: 'Fortinet', product: 'FortiSIEM', category: 'analytics' }
  ],
  DEVICES: [
    // MDM / UEM (mdm)
    { name: 'Microsoft', product: 'Microsoft Intune', category: 'mdm' },
    { name: 'Jamf', product: 'Jamf Pro', category: 'mdm' },
    { name: 'VMware', product: 'Workspace ONE', category: 'mdm' },
    { name: 'IBM', product: 'IBM MaaS360', category: 'mdm' },
    { name: 'Ivanti', product: 'Ivanti Neurons for UEM', category: 'mdm' },
    { name: 'Citrix', product: 'Citrix Endpoint Management', category: 'mdm' },
    { name: 'Kandji', product: 'Kandji', category: 'mdm' },
    { name: 'SOTI', product: 'SOTI MobiControl', category: 'mdm' },
    { name: 'ManageEngine', product: 'ManageEngine MDM', category: 'mdm' },
    // Compliance & Vulnerability (compliance)
    { name: 'Tanium', product: 'Tanium Comply', category: 'compliance' },
    { name: 'Qualys', product: 'Qualys VMDR', category: 'compliance' },
    { name: 'Tenable', product: 'Tenable Nessus', category: 'compliance' },
    { name: 'Rapid7', product: 'Rapid7 InsightVM', category: 'compliance' },
    { name: 'Tripwire', product: 'Tripwire Enterprise', category: 'compliance' },
    { name: 'HCL', product: 'HCL BigFix', category: 'compliance' },
    // EDR / XDR (edr)
    { name: 'CrowdStrike', product: 'CrowdStrike Falcon', category: 'edr' },
    { name: 'SentinelOne', product: 'SentinelOne Singularity', category: 'edr' },
    { name: 'Palo Alto', product: 'Cortex XDR', category: 'edr' },
    { name: 'Microsoft', product: 'Microsoft Defender for Endpoint', category: 'edr' },
    { name: 'Cisco', product: 'Cisco Secure Endpoint', category: 'edr' },
    { name: 'Fortinet', product: 'FortiEDR', category: 'edr' },
    { name: 'Cybereason', product: 'Cybereason EDR', category: 'edr' },
    { name: 'Trellix', product: 'Trellix EDR', category: 'edr' },
    { name: 'Sophos', product: 'Sophos Intercept X', category: 'edr' },
    { name: 'VMware', product: 'VMware Carbon Black', category: 'edr' },
    { name: 'Trend Micro', product: 'Trend Micro Vision One', category: 'edr' },
    { name: 'Elastic', product: 'Elastic Endpoint Security', category: 'edr' },
    // Asset Inventory (asset)
    { name: 'Axonius', product: 'Axonius', category: 'asset' },
    { name: 'ServiceNow', product: 'ServiceNow ITAM', category: 'asset' },
    { name: 'Oomnitza', product: 'Oomnitza', category: 'asset' },
    { name: 'Flexera', product: 'Flexera One', category: 'asset' },
    { name: 'Lansweeper', product: 'Lansweeper', category: 'asset' },
    { name: 'runZero', product: 'runZero', category: 'asset' },
    { name: 'JupiterOne', product: 'JupiterOne', category: 'asset' },
    // ZTNA (ztna)
    { name: 'Zscaler', product: 'Zscaler Private Access', category: 'ztna' },
    { name: 'Palo Alto', product: 'Prisma Access', category: 'ztna' },
    { name: 'Netskope', product: 'Netskope Private Access', category: 'ztna' },
    { name: 'Cloudflare', product: 'Cloudflare Access', category: 'ztna' },
    { name: 'Cisco', product: 'Cisco Secure Access', category: 'ztna' },
    { name: 'Fortinet', product: 'FortiSASE', category: 'ztna' },
    { name: 'Cato Networks', product: 'Cato SASE Cloud', category: 'ztna' },
    { name: 'Appgate', product: 'Appgate SDP', category: 'ztna' }
  ],
  NETWORKS: [
    // Network Segmentation (segmentation)
    { name: 'Cisco', product: 'Cisco ISE / TrustSec', category: 'segmentation' },
    { name: 'Palo Alto', product: 'Palo Alto NGFW', category: 'segmentation' },
    { name: 'Fortinet', product: 'FortiGate', category: 'segmentation' },
    { name: 'Check Point', product: 'Check Point Quantum', category: 'segmentation' },
    { name: 'Juniper', product: 'Juniper SRX', category: 'segmentation' },
    { name: 'Arista', product: 'Arista CloudVision', category: 'segmentation' },
    { name: 'VMware', product: 'VMware NSX', category: 'segmentation' },
    { name: 'Aruba', product: 'Aruba ClearPass', category: 'segmentation' },
    { name: 'Forescout', product: 'Forescout eyeSight', category: 'segmentation' },
    { name: 'Fortinet', product: 'FortiNAC', category: 'segmentation' },
    // NDR / Traffic Analysis (ndr)
    { name: 'Cisco', product: 'Secure Network Analytics', category: 'ndr' },
    { name: 'Darktrace', product: 'Darktrace', category: 'ndr' },
    { name: 'Vectra AI', product: 'Vectra AI', category: 'ndr' },
    { name: 'ExtraHop', product: 'ExtraHop Reveal(x)', category: 'ndr' },
    { name: 'Corelight', product: 'Corelight', category: 'ndr' },
    { name: 'Gigamon', product: 'Gigamon', category: 'ndr' },
    { name: 'Palo Alto', product: 'Cortex XSIAM', category: 'ndr' },
    // Microsegmentation (microseg)
    { name: 'Illumio', product: 'Illumio Core', category: 'microseg' },
    { name: 'Akamai', product: 'Guardicore (Akamai)', category: 'microseg' },
    { name: 'Cisco', product: 'Cisco Secure Workload', category: 'microseg' },
    { name: 'VMware', product: 'VMware NSX DFW', category: 'microseg' },
    { name: 'ColorTokens', product: 'ColorTokens Xshield', category: 'microseg' },
    { name: 'Zscaler', product: 'Zscaler Workload Segmentation', category: 'microseg' },
    // Traffic Encryption (encryption)
    { name: 'HashiCorp', product: 'Vault (PKI)', category: 'encryption' },
    { name: 'Cisco', product: 'Cisco MACsec', category: 'encryption' },
    { name: 'Fortinet', product: 'FortiGate IPsec', category: 'encryption' },
    { name: 'Palo Alto', product: 'GlobalProtect', category: 'encryption' },
    { name: 'Thales', product: 'Thales HSE', category: 'encryption' },
    // DDoS & Resilience (resilience)
    { name: 'Cloudflare', product: 'Cloudflare Magic Transit', category: 'resilience' },
    { name: 'Akamai', product: 'Akamai Prolexic', category: 'resilience' },
    { name: 'AWS', product: 'AWS Shield', category: 'resilience' },
    { name: 'Radware', product: 'Radware DefensePro', category: 'resilience' },
    { name: 'F5', product: 'F5 Silverline', category: 'resilience' },
    { name: 'NETSCOUT', product: 'NETSCOUT Arbor', category: 'resilience' }
  ],
  APPLICATIONS: [
    // ZTNA / SSO (ztna)
    { name: 'Zscaler', product: 'Zscaler ZPA', category: 'ztna' },
    { name: 'Cloudflare', product: 'Cloudflare Access', category: 'ztna' },
    { name: 'Palo Alto', product: 'Prisma Access Browser', category: 'ztna' },
    { name: 'Netskope', product: 'Netskope CASB', category: 'ztna' },
    { name: 'Microsoft', product: 'Entra Private Access', category: 'ztna' },
    { name: 'Citrix', product: 'Citrix Secure Access', category: 'ztna' },
    // WAF / API Security (waf)
    { name: 'Cloudflare', product: 'Cloudflare WAF', category: 'waf' },
    { name: 'Akamai', product: 'Akamai App & API Protector', category: 'waf' },
    { name: 'AWS', product: 'AWS WAF', category: 'waf' },
    { name: 'Imperva', product: 'Imperva WAF', category: 'waf' },
    { name: 'F5', product: 'F5 Advanced WAF', category: 'waf' },
    { name: 'Fortinet', product: 'FortiWeb', category: 'waf' },
    { name: 'Barracuda', product: 'Barracuda WAF', category: 'waf' },
    { name: 'Radware', product: 'Radware AppWall', category: 'waf' },
    { name: 'Kong', product: 'Kong Gateway', category: 'waf' },
    { name: 'Google', product: 'Google Cloud Armor', category: 'waf' },
    { name: 'Microsoft', product: 'Azure WAF', category: 'waf' },
    // RASP (rasp)
    { name: 'Contrast', product: 'Contrast Security', category: 'rasp' },
    { name: 'Dynatrace', product: 'Dynatrace Application Security', category: 'rasp' },
    { name: 'Imperva', product: 'Imperva RASP', category: 'rasp' },
    { name: 'Fastly', product: 'Signal Sciences (Fastly)', category: 'rasp' },
    // DevSecOps (devsec)
    { name: 'Snyk', product: 'Snyk Code', category: 'devsec' },
    { name: 'Veracode', product: 'Veracode', category: 'devsec' },
    { name: 'Checkmarx', product: 'Checkmarx', category: 'devsec' },
    { name: 'SonarSource', product: 'SonarQube', category: 'devsec' },
    { name: 'Synopsys', product: 'Synopsys Black Duck', category: 'devsec' },
    { name: 'Mend', product: 'Mend SCA', category: 'devsec' },
    { name: 'Aqua Security', product: 'Aqua Security', category: 'devsec' },
    { name: 'Palo Alto', product: 'Prisma Cloud', category: 'devsec' },
    { name: 'GitHub', product: 'GitHub Advanced Security', category: 'devsec' },
    { name: 'GitLab', product: 'GitLab Ultimate', category: 'devsec' },
    { name: 'JFrog', product: 'JFrog Xray', category: 'devsec' },
    { name: 'Semgrep', product: 'Semgrep', category: 'devsec' }
  ],
  DATA: [
    // Data Discovery (discovery)
    { name: 'BigID', product: 'BigID', category: 'discovery' },
    { name: 'Securiti', product: 'Securiti.ai', category: 'discovery' },
    { name: 'OneTrust', product: 'OneTrust Discovery', category: 'discovery' },
    { name: 'Spirion', product: 'Spirion', category: 'discovery' },
    { name: 'AWS', product: 'Amazon Macie', category: 'discovery' },
    { name: 'Google', product: 'Google Cloud DLP', category: 'discovery' },
    // Data Classification (classification)
    { name: 'Microsoft', product: 'Microsoft Purview', category: 'classification' },
    { name: 'Titus', product: 'Titus Classification', category: 'classification' },
    { name: 'Boldon James', product: 'Boldon James Classifier', category: 'classification' },
    { name: 'Fortra', product: 'Fortra Digital Guardian', category: 'classification' },
    { name: 'PKWARE', product: 'PKWARE PK Protect', category: 'classification' },
    // DLP (dlp)
    { name: 'Varonis', product: 'Varonis DatAdvantage', category: 'dlp' },
    { name: 'Symantec', product: 'Symantec DLP', category: 'dlp' },
    { name: 'Microsoft', product: 'Microsoft Purview DLP', category: 'dlp' },
    { name: 'Netskope', product: 'Netskope DLP', category: 'dlp' },
    { name: 'Zscaler', product: 'Zscaler Data Protection', category: 'dlp' },
    { name: 'Palo Alto', product: 'Prisma SaaS DLP', category: 'dlp' },
    { name: 'Proofpoint', product: 'Proofpoint DLP', category: 'dlp' },
    { name: 'Code42', product: 'Code42 Incydr', category: 'dlp' },
    { name: 'DTEX', product: 'DTEX InTERCEPT', category: 'dlp' },
    // Encryption / Key Management (encryption)
    { name: 'HashiCorp', product: 'HashiCorp Vault', category: 'encryption' },
    { name: 'AWS', product: 'AWS KMS', category: 'encryption' },
    { name: 'Google', product: 'Google Cloud KMS', category: 'encryption' },
    { name: 'Microsoft', product: 'Azure Key Vault', category: 'encryption' },
    { name: 'Thales', product: 'Thales CipherTrust', category: 'encryption' },
    { name: 'Entrust', product: 'Entrust KeyControl', category: 'encryption' },
    { name: 'Fortanix', product: 'Fortanix DSM', category: 'encryption' },
    // Backup / DR (backup)
    { name: 'Veeam', product: 'Veeam', category: 'backup' },
    { name: 'Cohesity', product: 'Cohesity', category: 'backup' },
    { name: 'Commvault', product: 'Commvault', category: 'backup' },
    { name: 'Rubrik', product: 'Rubrik', category: 'backup' },
    { name: 'Druva', product: 'Druva', category: 'backup' },
    { name: 'Veritas', product: 'Veritas NetBackup', category: 'backup' },
    { name: 'Zerto', product: 'Zerto', category: 'backup' },
    { name: 'AWS', product: 'AWS Backup', category: 'backup' },
    { name: 'Microsoft', product: 'Azure Backup', category: 'backup' }
  ]
};

export default PILLAR_TEMPLATES;
